/**
 * useSearch hook for searching posts
 *
 * Provides infinite scroll search functionality with various filter options.
 */

import { useInfiniteQuery } from '@tanstack/react-query'
import { search } from '@/services/steemworld'
import { transformFeedResponse } from '@/services/feeds/transformer'
import { queryKeys } from '@/services/queryClient'
import { Post } from '@/types/post'
import type { SearchField, SearchSortField, SearchSortOrder } from '@/services/steemworld'

export interface UseSearchOptions {
  /** Search query text */
  query: string
  /** Filter by author username */
  author?: string
  /** Filter by tags (comma-separated) */
  tags?: string
  /** Field to search in */
  searchIn?: SearchField
  /** Sort field */
  sortBy?: SearchSortField
  /** Sort order */
  order?: SearchSortOrder
  /** Number of items per page */
  limit?: number
  /** Enable/disable the query */
  enabled?: boolean
}

export interface SearchResult {
  posts: Post[]
  nextOffset: number
  hasMore: boolean
}

export function useSearch({
  query,
  author,
  tags,
  searchIn = 'title,body',
  sortBy = 'created',
  order = 'desc',
  limit = 20,
  enabled = true,
}: UseSearchOptions) {
  const filters = { author, tags, searchIn, sortBy, order }

  return useInfiniteQuery({
    queryKey: queryKeys.searchInfinite(query, filters),
    queryFn: async ({ pageParam = 0 }): Promise<SearchResult> => {
      const response = await search({
        query,
        author,
        tags,
        searchIn,
        params: {
          offset: pageParam,
          limit,
          bodyLength: 250,
          sortBy,
          order,
        },
      })

      const rows = response?.result?.rows || []
      const posts = transformFeedResponse(rows)

      return {
        posts,
        nextOffset: pageParam + limit,
        hasMore: posts.length === limit,
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.nextOffset : undefined
    },
    initialPageParam: 0,
    enabled: enabled && query.trim().length > 0,
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Helper to get all posts from infinite search query
 */
export function getAllSearchPosts(
  data: { pages: SearchResult[] } | undefined
): Post[] {
  if (!data) return []
  return data.pages.flatMap((page) => page.posts)
}

/**
 * Simple search hook for single-page results (no infinite scroll)
 */
export function useSearchPosts({
  query,
  author,
  tags,
  searchIn = 'title,body',
  sortBy = 'created',
  order = 'desc',
  limit = 20,
  offset = 0,
  enabled = true,
}: UseSearchOptions & { offset?: number }) {
  const filters = { author, tags, searchIn, sortBy, order, offset }

  return useInfiniteQuery({
    queryKey: queryKeys.search(query, filters),
    queryFn: async (): Promise<SearchResult> => {
      const response = await search({
        query,
        author,
        tags,
        searchIn,
        params: {
          offset,
          limit,
          bodyLength: 250,
          sortBy,
          order,
        },
      })

      const rows = response?.result?.rows || []
      const posts = transformFeedResponse(rows)

      return {
        posts,
        nextOffset: offset + limit,
        hasMore: posts.length === limit,
      }
    },
    getNextPageParam: () => undefined,
    initialPageParam: 0,
    enabled: enabled && query.trim().length > 0,
    staleTime: 60 * 1000,
  })
}
